/**
 * computeWritingStatistics
 * Pure helpers behind the /stats page: best streak, lifetime totals and
 * posts-per-week buckets, all derived client-side from the user's own posts
 * (same UTC day convention as computeWritingStreak). Dependency-free so the
 * self-check at the bottom runs with plain node:
 *   node client/src/utils/computeWritingStatistics.js
 */

const DAY_MS = 86400000;
const WEEK_MS = 7 * DAY_MS;
const dayKey = (ms) => new Date(ms).toISOString().slice(0, 10);

/** Posts that count as writing: originals only, reposts excluded. */
const originalPosts = (posts) => (posts || []).filter((p) => !p.repostOf && p.createdAt);

/** Midnight UTC (ms) of the day a timestamp falls on. */
const utcMidnight = (value) => {
  const d = new Date(value);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/** Longest ever run of consecutive UTC days with at least one original post. */
export function computeBestStreak(posts) {
  const days = [...new Set(originalPosts(posts).map((p) => utcMidnight(p.createdAt)))].sort((a, b) => a - b);
  let best = 0;
  let run = 0;
  for (let i = 0; i < days.length; i++) {
    run = i > 0 && days[i] - days[i - 1] === DAY_MS ? run + 1 : 1;
    if (run > best) best = run;
  }
  return best;
}

/** Lifetime totals over original posts. Words split on whitespace. */
export function computeWritingTotals(posts) {
  const own = originalPosts(posts);
  return {
    postsWritten: own.length,
    wordsWritten: own.reduce(
      (sum, p) => sum + String(p.content || '').split(/\s+/).filter(Boolean).length,
      0
    ),
    likesReceived: own.reduce((sum, p) => sum + (p.likes ? p.likes.length : 0), 0),
    commentsReceived: own.reduce((sum, p) => sum + (p.comments ? p.comments.length : 0), 0)
  };
}

/**
 * Original-posts-per-week for the last `weekCount` weeks, oldest first;
 * the last bucket is the current week. Weeks start Monday 00:00 UTC.
 * @returns {Array<{weekStart: string, count: number}>} weekStart as YYYY-MM-DD
 */
export function bucketPostsPerWeek(posts, weekCount = 8, now = Date.now()) {
  // Monday 00:00 UTC of the week a timestamp falls in.
  const startOfWeek = (value) => {
    const midnight = utcMidnight(value);
    return midnight - ((new Date(midnight).getUTCDay() + 6) % 7) * DAY_MS;
  };

  const firstWeekStart = startOfWeek(now) - (weekCount - 1) * WEEK_MS;
  const buckets = Array.from({ length: weekCount }, (_, i) => ({
    weekStart: dayKey(firstWeekStart + i * WEEK_MS),
    count: 0
  }));

  for (const p of originalPosts(posts)) {
    const index = Math.floor((startOfWeek(p.createdAt) - firstWeekStart) / WEEK_MS);
    if (index >= 0 && index < weekCount) buckets[index].count++;
  }
  return buckets;
}

// ponytail: assert-based self-check, same spirit as serveReactBuildInProduction.js.
// ESM has no require.main, so "run directly" is detected via process.argv (the
// typeof guard keeps this dead code in the browser bundle).
if (typeof process !== 'undefined' && process.argv && process.argv[1] && process.argv[1].endsWith('computeWritingStatistics.js')) {
  const assert = (cond, msg) => { if (!cond) throw new Error(`Self-check failed: ${msg}`); };
  const BASE = Date.UTC(2026, 5, 30, 12); // Tue 30 Jun 2026, noon UTC
  const post = (daysAgo, extra) => ({ createdAt: new Date(BASE - daysAgo * DAY_MS).toISOString(), ...extra });

  // computeBestStreak
  assert(computeBestStreak([]) === 0, 'no posts -> streak 0');
  assert(computeBestStreak([post(0)]) === 1, 'single day -> 1');
  assert(computeBestStreak([post(0), post(1), post(3), post(4), post(5)]) === 3, 'longest run wins');
  assert(computeBestStreak([post(2), post(2), post(3)]) === 2, 'same-day duplicates collapse');
  assert(computeBestStreak([post(0, { repostOf: 'x' })]) === 0, 'reposts do not count');

  // computeWritingTotals
  const totals = computeWritingTotals([
    post(0, { content: 'one two  three', likes: ['a', 'b'], comments: [{}] }),
    post(1, { content: '  four ', likes: [], comments: [] }),
    post(2, { content: 'not counted', repostOf: 'x', likes: ['a'], comments: [{}] })
  ]);
  assert(totals.postsWritten === 2, 'repost excluded from post count');
  assert(totals.wordsWritten === 4, 'words split on whitespace');
  assert(totals.likesReceived === 2, 'likes summed over originals');
  assert(totals.commentsReceived === 1, 'comments summed over originals');
  assert(computeWritingTotals([]).wordsWritten === 0, 'empty totals are zero');

  // bucketPostsPerWeek — BASE is a Tuesday, so its week starts Mon 29 Jun 2026.
  const weeks = bucketPostsPerWeek(
    [post(0), post(1), post(8), post(0, { repostOf: 'x' }), { createdAt: '2020-01-01T00:00:00Z' }],
    8,
    BASE
  );
  assert(weeks.length === 8, 'eight buckets');
  assert(weeks[7].weekStart === '2026-06-29', 'last bucket is the current week (Monday key)');
  assert(weeks[7].count === 2, 'current week counts Mon+Tue posts, not the repost');
  assert(weeks[6].count === 1, 'previous week counted');
  assert(weeks[0].count === 0, 'ancient post falls outside the window');
  assert(weeks[0].weekStart === '2026-05-11', 'first bucket is 7 weeks back');

  console.log('computeWritingStatistics self-check OK');
}
